import useAppContext from 'flow/hooks/useAppContext';
import { memo, MouseEvent } from 'react';
import NestedMenuItem from '../ui-components/NestedMenuItem';
import { TypedMap } from 'flow/types/TypedMap';
import { Contact } from 'flow/types/Contact';
import MenuItem from 'flow/components/ui-components/MenuItem';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { getKeyboardShortcuts } from 'flow/selectors/keyboard-shortcuts-selectors';
import { normalizeShortcutToDisplay } from 'flow/utils/shortcut-utils';
import {
    getIsPhoneConnected,
    getOwnActiveCalls,
    hasActiveCallsBlockingNewCall,
} from 'flow/selectors/call-selectors';
import { useOutgoingCallsEnabled } from 'flow/hooks/api/useSoftphoneCallSettings';
import { isTeamsTypeIntegration } from 'flow/utils/app-utils';
import { isRegisteredSIP, NewCallBlockReason } from 'flow/utils/call-utils';
import {
    transferAttendedCall,
    transferBlindCall,
} from 'flow/actions/phone-actions';
import { getOwnExtension } from 'flow/selectors/selectors';

interface Props {
    openTransferSelectionDialog: (event: MouseEvent) => void;
    fixedNumber: string;
    mobileNumber: string;
    transferContact: TypedMap<Contact>;
}

type TransferType = 'blind' | 'attended';
type NumberType = 'fixed' | 'mobile';

const QuickTransferMenuItem = ({
    openTransferSelectionDialog,
    fixedNumber,
    mobileNumber,
    transferContact,
}: Props) => {
    const { i18next, embedded } = useAppContext();
    const ownExtension = useAppSelector(getOwnExtension);
    const dispatch = useAppDispatch();

    const hasActiveCall = useAppSelector(getOwnActiveCalls).size > 0;
    const phoneConnected = useAppSelector(getIsPhoneConnected);
    const shortcuts = useAppSelector(getKeyboardShortcuts);
    const outgoingCallsEnabled = useOutgoingCallsEnabled();
    const isActiveCallsBlockingNewCall = useAppSelector(
        (state) =>
            hasActiveCallsBlockingNewCall(state, 'outgoing') !==
            NewCallBlockReason.NONE,
    );

    if (
        !hasActiveCall ||
        (!fixedNumber && !mobileNumber) ||
        isTeamsTypeIntegration(embedded) ||
        (!phoneConnected && !isRegisteredSIP(ownExtension)) ||
        !isActiveCallsBlockingNewCall
    ) {
        return null;
    }

    if (!outgoingCallsEnabled) {
        return (
            <MenuItem disabled={true}>
                {i18next.t('settings.softphone.outgoing_calls_disabled')}
            </MenuItem>
        );
    }

    const displayShortcutKey = (type: string) => {
        const matchedShortcut = shortcuts.find(
            (shortcut) =>
                shortcut.get('enabled') && shortcut.get('label') === type,
        );
        return normalizeShortcutToDisplay(matchedShortcut.get('value'));
    };

    const handleTransferCall = (
        phoneNumber: string,
        transferType: TransferType,
    ) => {
        if (transferType === 'attended') {
            dispatch(transferAttendedCall(phoneNumber, transferContact));
        } else {
            dispatch(transferBlindCall(phoneNumber));
        }
    };

    const quickTransferContextMenuOption = (
        phoneNumber: string,
        numberType: NumberType,
        transferType: TransferType,
    ) => {
        const phoneNumberText =
            numberType === 'mobile'
                ? i18next.t('settings.mobile_number')
                : i18next.t('settings.landline_number');
        return (
            <MenuItem
                onClick={() => handleTransferCall(phoneNumber, transferType)}
            >
                <p className="flex w-full justify-between gap-1 whitespace-pre">
                    <span className="flex-1">
                        {`${
                            transferType === 'attended'
                                ? i18next.t(
                                      'General.Call.Transfer.Alert.Attended',
                                  )
                                : i18next.t('General.Call.Transfer.Alert.Blind')
                        } - ${phoneNumberText}`}
                    </span>
                    <span className="text-gray-400">
                        {displayShortcutKey(
                            `${transferType}_transfer_${numberType}`,
                        )}
                    </span>
                </p>
            </MenuItem>
        );
    };

    return (
        <NestedMenuItem title={i18next.t('General.Call.Transfer')}>
            <MenuItem onClick={openTransferSelectionDialog}>
                <p className="flex w-full justify-between gap-1 whitespace-pre">
                    <span className="flex-1">
                        {`${i18next.t('General.Call.Transfer')}...`}
                    </span>
                    <span className="text-gray-400">
                        {displayShortcutKey('open_transfer_modal')}
                    </span>
                </p>
            </MenuItem>
            {fixedNumber &&
                quickTransferContextMenuOption(
                    fixedNumber,
                    'fixed',
                    'attended',
                )}
            {mobileNumber &&
                quickTransferContextMenuOption(
                    mobileNumber,
                    'mobile',
                    'attended',
                )}
            {fixedNumber &&
                quickTransferContextMenuOption(fixedNumber, 'fixed', 'blind')}
            {mobileNumber &&
                quickTransferContextMenuOption(mobileNumber, 'mobile', 'blind')}
        </NestedMenuItem>
    );
};

export default memo(QuickTransferMenuItem);
